import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';
import { queryClient } from './lib/queryClient';
import { wsService } from './services/websocket';
import { ToastProvider } from './components/Toast';
import AppErrorBoundary from './components/AppErrorBoundary';
import ApiBridge from './components/ApiBridge';
import NotFound from './pages/NotFound';
import Layout from './components/Layout';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import StudentDetail from './pages/StudentDetail';
import Labs from './pages/Labs';
import Environments from './pages/Environments';
import Grading from './pages/Grading';
import Announcements from './pages/Announcements';
import Messages from './pages/Messages';
import BroadcastComposer from './pages/BroadcastComposer';
import GradeAppeals from './pages/GradeAppeals';
import Cohorts from './pages/Cohorts';
import CohortDetail from './pages/CohortDetail';
import HitlQueue from './pages/hitl/HitlQueue';
import HitlSession from './pages/hitl/HitlSession';
import HitlHandback from './pages/hitl/HitlHandback';
import HitlReport from './pages/hitl/HitlReport';
import HitlSettings from './pages/hitl/HitlSettings';
import AtRiskWidget from './components/AtRiskWidget';
import { HITL_ENABLED } from './config/features';
import { resolveInstructorAccess } from './lib/reviewerAccess';

const AtRiskWidgetStandalone: React.FC = () => <AtRiskWidget standalone />;

const PureReviewerSurface: React.FC = () => {
  const oidc = useAuth();
  const profile = (oidc.user?.profile ?? {}) as Record<string, unknown>;
  const identity =
    (profile.email as string)
    || (profile.preferred_username as string)
    || 'reviewer';

  const switchIdentity = () => {
    oidc.signoutRedirect().catch(async () => {
      await oidc.removeUser();
      window.location.assign('/login');
    });
  };

  return (
    <div>
      <header
        style={{
          minHeight: 64,
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          borderBottom: '1px solid #e0e0e0',
          background: '#fff',
        }}
      >
        <div>
          <strong>Grade appeal review</strong>
          <div style={{ fontSize: 12, color: '#6b6b6b', marginTop: 2 }}>
            Read-only reviewer · {identity}
          </div>
        </div>
        <button type="button" onClick={switchIdentity}>
          Sign out / switch identity
        </button>
      </header>
      <GradeAppeals />
    </div>
  );
};

function AppContent() {
  const oidc = useAuth();

  // ZewstID is the sole identity provider post-cutover. Admission reads only
  // `resource_access` under the configured instructor-dashboard client.
  // Generic profile.role and unrelated-client roles never grant access.
  const profile = (oidc.user?.profile ?? {}) as Record<string, any>;
  const accessMode = resolveInstructorAccess(profile);
  const hasStaffAccess =
    oidc.isAuthenticated && accessMode === 'staff';
  const isPureReviewer =
    oidc.isAuthenticated && accessMode === 'reviewer';
  const hasAccess = hasStaffAccess || isPureReviewer;

  // Connect WebSocket when authenticated
  useEffect(() => {
    if (hasStaffAccess) {
      wsService.connect();
    }
    return () => {
      wsService.disconnect();
    };
  }, [hasStaffAccess]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/login" element={
          hasAccess
            ? <Navigate
                to={isPureReviewer ? '/grades/appeals' : '/'}
                replace
              />
            : <Login />
        } />
        {isPureReviewer ? (
          <>
            {/* Pure reviewers get one deliberately read-only surface. They do
                not enter Layout or inherit instructor navigation/routes. */}
            <Route
              path="/"
              element={<Navigate to="/grades/appeals" replace />}
            />
            <Route
              path="/grades/appeals"
              element={<PureReviewerSurface />}
            />
            <Route
              path="*"
              element={<Navigate to="/grades/appeals" replace />}
            />
          </>
        ) : (
          <>
            <Route path="/" element={
              hasStaffAccess ? <Layout /> : <Navigate to="/login" replace />
            }>
              {/* Per Figma "06 · Instructor", the primary landing is the Cohort
                  Dashboard (roster + KPI strip + at-risk rail). The legacy
                  analytics Dashboard still exists but is reachable only via the
                  "More" menu → Overview. */}
              <Route index element={<Cohorts />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="overview" element={<Dashboard />} />
              <Route path="students" element={<Students />} />
              <Route path="students/:studentId" element={<StudentDetail />} />
              <Route path="cohorts" element={<Cohorts />} />
              <Route path="cohorts/:cohortId" element={<CohortDetail />} />
              <Route path="labs" element={<Labs />} />
              <Route path="environments" element={<Environments />} />
              <Route path="grading" element={<Grading />} />
              <Route path="announcements" element={<Announcements />} />
              <Route path="messages" element={<Messages />} />
              {/* HITL is a strict rollout opt-in shared with the learner app and
                  backend. Keeping every route behind one flag prevents direct
                  links from exposing a dead queue before runtime readiness. */}
              {HITL_ENABLED && (
                <>
                  <Route path="hitl" element={<HitlQueue />} />
                  <Route path="hitl/session/:id" element={<HitlSession />} />
                  <Route path="hitl/session/:id/handback" element={<HitlHandback />} />
                  <Route path="hitl/session/:id/report" element={<HitlReport />} />
                  <Route path="hitl/settings" element={<HitlSettings />} />
                </>
              )}
              {/* MV5 Sprint 2.C — broadcast composer + grade appeals queue. */}
              <Route path="messages/broadcast" element={<BroadcastComposer />} />
              <Route path="grades/appeals" element={<GradeAppeals />} />
              {/* Standalone embed of the At-Risk widget (Figma frame 175:828 —
                  500×420 card). Used by product pages that embed the widget
                  on marketing / demo surfaces. */}
              <Route
                path="widgets/at-risk"
                element={
                  <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
                    <AtRiskWidgetStandalone />
                  </div>
                }
              />
            </Route>
            <Route path="*" element={<NotFound />} />
          </>
        )}
      </Routes>
    </BrowserRouter>
  );
}

// ZewstID OIDC config (instructor-dashboard client). Same pattern as web-ide:
// redirect_uri derived at runtime so the same build works for localhost dev
// and prod (instructor.hauraki.ai) without rebuilding.
const zewstidOidcConfig = {
  authority: process.env.REACT_APP_ZEWSTID_AUTHORITY || 'https://auth.zewstid.com/realms/zewstid',
  client_id: process.env.REACT_APP_ZEWSTID_CLIENT_ID || '',
  redirect_uri:
    typeof window !== 'undefined'
      ? `${window.location.origin}/auth/callback`
      : 'http://localhost:3006/auth/callback',
  post_logout_redirect_uri:
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3006',
  // Scope is fixed for this app. It is intentionally a hardcoded constant (not an
  // env override): a prod build once baked an invalid `hauraki` scope via
  // REACT_APP_ZEWSTID_SCOPE, which ZewstID rejects ("Invalid scopes") and blocked
  // login entirely. The app never needs any scope beyond these four.
  scope: 'openid profile email offline_access',
  response_type: 'code',
  userStore:
    typeof window !== 'undefined' ? new WebStorageStateStore({ store: window.sessionStorage }) : undefined,
  automaticSilentRenew: true,
  loadUserInfo: true,
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

function App() {
  return (
    <AppErrorBoundary>
      <AuthProvider {...zewstidOidcConfig}>
        <ApiBridge />
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <AppContent />
          </ToastProvider>
        </QueryClientProvider>
      </AuthProvider>
    </AppErrorBoundary>
  );
}

export default App;
